/**
 * Telehealth Service
 * 
 * Handles API calls for telehealth video appointments.
 * Uses Daily.co for video calls.
 */

import api from './api';

export interface TelehealthTokenResponse {
  token: string;
  roomName: string;
  roomUrl: string;
  identity: string;
  displayName: string;
  role: 'provider' | 'patient';
  isOwner: boolean;
  appointment: {
    id: string;
    patientName?: string;
    providerName?: string;
    clinicName?: string;
    startTime: string;
    duration: number;
    status: string;
    patientNotes?: string;
    title?: string;
  };
}

export interface WaitingRoomStatus {
  appointmentId: string;
  startTime: string;
  providerName: string;
  clinicName: string;
  roomReady: boolean;
  providerJoined: boolean;
  status: string;
  canJoin: boolean;
  // Additional fields for consent checking
  patientId?: string;
  clinicId?: string;
  patientName?: string;
}

export interface RoomStatus {
  roomName: string;
  roomUrl: string;
  roomStatus: string;
  appointmentStatus: string;
  participantCount: number;
  participants: Array<{
    sessionId: string;
    userId: string;
    userName: string;
    joinTime: string;
  }>;
}

export interface TelehealthConfig {
  video: {
    isConfigured: boolean;
    hasApiKey: boolean;
    apiUrl: string;
    roomExpiryHours: number;
    maxParticipants: number;
    apiConnected?: boolean;
  };
  sms: {
    isConfigured: boolean;
    hasPhoneNumber: boolean;
  };
}

class TelehealthService {
  /**
   * Get video access token for provider to join a call
   */
  async getProviderToken(appointmentId: string): Promise<TelehealthTokenResponse> {
    const response = await api.get(`/telehealth/token/${appointmentId}`);
    return response.data.data;
  }

  /**
   * Get video access token for patient to join a call (public endpoint)
   */
  async getPatientToken(appointmentId: string, joinToken: string): Promise<TelehealthTokenResponse> {
    const response = await api.get(`/telehealth/join/${appointmentId}`, {
      params: { token: joinToken }
    });
    return response.data.data;
  }

  /**
   * Get video access token via join link (public endpoint, supports both patient and provider)
   * Used when joining from calendar/email links with token-based authentication
   */
  async getJoinToken(appointmentId: string, joinToken: string, role: 'patient' | 'provider' = 'patient'): Promise<TelehealthTokenResponse> {
    const response = await api.get(`/telehealth/join/${appointmentId}`, {
      params: { token: joinToken, role }
    });
    return response.data.data;
  }

  /**
   * Check waiting room status (is provider ready?)
   */
  async checkWaitingRoom(appointmentId: string, joinToken: string): Promise<WaitingRoomStatus> {
    const response = await api.get(`/telehealth/waiting/${appointmentId}`, {
      params: { token: joinToken }
    });
    return response.data.data;
  }

  /**
   * Create a video room for an appointment
   */
  async createRoom(appointmentId: string): Promise<{
    roomName: string;
    roomUrl: string;
    joinToken: string;
    joinLink: string;
    expiresAt: string;
  }> {
    const response = await api.post('/telehealth/rooms', { appointmentId });
    return response.data.data;
  }

  /**
   * Get room status and participants
   */
  async getRoomStatus(appointmentId: string): Promise<RoomStatus> {
    const response = await api.get(`/telehealth/rooms/${appointmentId}/status`);
    return response.data.data;
  }

  /**
   * End a video call and set appointment status
   * @param status - The appointment status to set ('completed' or 'no_show')
   */
  async endRoom(appointmentId: string, status: 'completed' | 'no_show' = 'completed'): Promise<{ success: boolean; appointmentStatus: string }> {
    const response = await api.post(`/telehealth/rooms/${appointmentId}/end`, { status });
    return response.data.data;
  }

  /**
   * End a video call via public join token (for providers joining via link)
   * @param joinToken - The join token from the URL
   * @param status - The appointment status to set ('completed' or 'no_show')
   */
  async endRoomPublic(appointmentId: string, joinToken: string, status: 'completed' | 'no_show' = 'completed'): Promise<{ success: boolean; appointmentStatus: string }> {
    const response = await api.post(`/telehealth/join/${appointmentId}/end`, { token: joinToken, status });
    return response.data.data;
  }

  /**
   * Check telehealth configuration status
   */
  async getConfiguration(): Promise<TelehealthConfig> {
    const response = await api.get('/telehealth/config');
    return response.data.data;
  }

  /**
   * Generate the join link for a patient
   */
  getPatientJoinLink(appointmentId: string, joinToken: string): string {
    const baseUrl = window.location.origin;
    return `${baseUrl}/telehealth/join/${appointmentId}?token=${joinToken}`;
  }
}

export const telehealthService = new TelehealthService();
export default telehealthService;
